<template>
  <div class="premium-dashboard">
    <div class="content-area" ref="scrollContainer" @wheel="pauseAutoScroll" @mousedown="pauseAutoScroll"
      @touchstart="pauseAutoScroll">
      <div class="global-header">
        <div class="page-title">Plant List</div>
        <div class="legend">
          <div class="auto-scroll-ctrl">
            <span class="ctrl-lbl">AUTO SCROLL</span>
            <v-switch v-model="autoScroll" dense hide-details color="#5AB4F0" class="mt-0 pt-0"></v-switch>
          </div>
          <div class="leg">
            <div class="ld don"></div>
            INV-ON
          </div>
          <div class="leg">
            <div class="ld doff"></div>
            INV-OFF
          </div>
          <div class="leg">
            <div class="ld dnc"></div>
            NO COMMUNICATION
          </div>
        </div>
      </div>
      <div v-if="pageLoading" class="centered-loader">
        <div class="loader-dots"><span></span><span></span><span></span></div>
        <div>CONNECTING...</div>
      </div>
      <div v-else-if="allPlants.length === 0 && !isRateLimited" class="empty-state">
        <div class="empty-state-card">
          <div class="empty-state-icon-wrap">
            <svg class="empty-state-icon" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="10" y="28" width="60" height="36" rx="4" stroke="currentColor" stroke-width="2" opacity="0.25" />
              <line x1="10" y1="40" x2="70" y2="40" stroke="currentColor" stroke-width="1.5" opacity="0.15" />
              <line x1="10" y1="52" x2="70" y2="52" stroke="currentColor" stroke-width="1.5" opacity="0.15" />
              <line x1="30" y1="28" x2="30" y2="64" stroke="currentColor" stroke-width="1.5" opacity="0.15" />
              <line x1="50" y1="28" x2="50" y2="64" stroke="currentColor" stroke-width="1.5" opacity="0.15" />
              <circle cx="40" cy="18" r="8" stroke="currentColor" stroke-width="2" opacity="0.3" />
              <line x1="40" y1="10" x2="40" y2="6" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                opacity="0.25" />
              <line x1="48" y1="14" x2="51" y2="11" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                opacity="0.25" />
              <line x1="32" y1="14" x2="29" y2="11" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                opacity="0.25" />
              <line x1="40" y1="26" x2="40" y2="28" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                opacity="0.2" />
              <path d="M35 70 L40 64 L45 70" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round" opacity="0.2" />
            </svg>
          </div>
          <div class="empty-state-title">No Plants Found</div>
          <div class="empty-state-subtitle">There are no active plants associated with your account.</div>
        </div>
      </div>
      <div v-else class="plant-list">
        <v-alert v-if="isRateLimited" type="warning" dense outline class="mb-3"
          style="background: rgba(255, 152, 0, 0.1); border-color: #ff9800; color: #ffbc00;">
          API rate limit reached. Pausing further plant loads and automatically retrying in the background...
        </v-alert>
        <div v-if="isRateLimited && allPlants.length === 0" class="centered-loader" style="padding-top: 20px;">
          <div class="loader-dots"><span style="background: #ff9800;"></span><span
              style="background: #ff9800;"></span><span style="background: #ff9800;"></span></div>
          <div style="color: #ff9800;">WAITING TO RETRY...</div>
        </div>
        <div v-for="(plant, pIdx) in visiblePlants" :key="'p-' + plant.plantId + '-' + pIdx" class="plant-section"
          ref="plantSections" :data-plant-id="plant.plantId">
          <div :class="['plant-header', { open: !!openedPlants[plant.plantId] }]" @click="togglePlant(plant.plantId)">
            <div class="ph-left">
              <div class="ph-info">
                <div class="ph-name">{{ plant.name }}</div>
                <div class="ph-loc">📍 {{ plant.location }}</div>
              </div>
            </div>
            <div class="ph-right">
              <div class="total-text-right">Total Inverters: <span class="tot-val">{{ plant.totalInverters }}</span>
              </div>
              <div class="stat-pills">
                <div v-if="!!openedPlants[plant.plantId]" class="expand-inverters-header-ctrl" @click.stop>
                  <span class="ctrl-lbl-header">EXPAND</span>
                  <v-switch :input-value="!!expandedInvertersPlants[plant.plantId]"
                    @change="toggleExpandInverters(plant.plantId)" dense hide-details color="#5AB4F0"
                    class="mt-0 pt-0"></v-switch>
                </div>
                <div :class="['stat-pill pill-on', { 'opacity-muted': plant.stats.on === 0 }]">
                  <div class="pill-dot pd-on"></div>
                  INV-ON: {{ plant.stats.on }}
                </div>
                <div :class="['stat-pill pill-off', { 'opacity-muted': plant.stats.off === 0 }]">
                  <div class="pill-dot pd-off"></div>
                  INV-OFF: {{ plant.stats.off }}
                </div>
                <div :class="['stat-pill pill-nc', { 'opacity-muted': plant.stats.nc === 0 }]">
                  <div class="pill-dot pd-nc"></div>
                  NO-COMM: {{ plant.stats.nc }}
                </div>
              </div>
              <div class="toggle-arrow">▾</div>
            </div>
          </div>
          <div v-if="!!openedPlants[plant.plantId]" class="plant-content">
            <div v-if="!plantTree[plant.plantId]" class="inner-loading">
              <div class="loader-dots">
                <span></span><span></span><span></span>
              </div>
              FETCHING DETAILS...
            </div>
            <div v-else class="plant-grid">
              <div v-for="(block, bIdx) in plantTree[plant.plantId]" :key="'b-' + block.block_id + '-' + bIdx"
                class="block-card">
                <div class="block-hdr">
                  {{ block.block_name }}
                </div>
                <div class="rooms-container">
                  <div v-for="(room, rIdx) in block.rooms" :key="'r-' + room.room_id + '-' + rIdx" class="room-row">
                    <div class="room-lbl">{{ room.room_name }}</div>
                    <div :class="['inv-row', { 'expanded': !!expandedInvertersPlants[plant.plantId] }]">
                      <template v-for="inv in room.inverters">
                        <div v-if="!!expandedInvertersPlants[plant.plantId]" :key="'e-' + inv.inverter_id" :class="[
                          'inv-card',
                          statusClass(plant.plantId, inv._statusKey),
                        ]">
                          <div class="inv-card-hdr">
                            <span class="inv-card-dot"></span>
                            <span class="inv-card-title">{{ inv.label }}</span>
                          </div>
                          <div class="inv-card-body">
                            <div class="inv-card-row">
                              <span class="row-label">AC Power</span><span class="row-val">{{
                                getInverterPower(
                                  plant.plantId,
                                  inv._statusKey,
                                  "ac",
                                )
                              }}</span>
                            </div>
                            <div class="inv-card-row">
                              <span class="row-label">DC Power</span><span class="row-val">{{
                                getInverterPower(
                                  plant.plantId,
                                  inv._statusKey,
                                  "dc",
                                )
                              }}</span>
                            </div>
                          </div>
                        </div>
                        <div v-else :key="'b-' + inv.inverter_id" :class="[
                          'inv-btn',
                          statusClass(plant.plantId, inv._statusKey),
                        ]" @mouseenter="
                          showHoverTooltip(
                            $event,
                            plant.plantId,
                            block,
                            room,
                            inv,
                          )
                          " @mouseleave="hideHoverTooltip">
                          <span class="inv-label">{{ inv.label }}</span>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="isLoadingBatch" class="batch-loader">
          <div class="loader-dots"><span></span><span></span><span></span></div>
          <div>LOADING MORE PLANTS...</div>
        </div>
        <div v-if="hasMorePlants" ref="sentinel" class="sentinel"></div>
      </div>
      <div v-show="hoverTooltip.visible" class="global-inv-tooltip" :style="hoverTooltip.style">
        <div class="tooltip-title">{{ hoverTooltip.title }}</div>
        <div class="tooltip-row">
          <span>AC Power</span><span>{{ hoverTooltip.acPower }}</span>
        </div>
        <div class="tooltip-row">
          <span>DC Power</span><span>{{ hoverTooltip.dcPower }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import inverterStatusApi from "@/services/inverterStatus.js";
const BATCH_LIMIT = 30;
const MQTT_TTL = 120;
const SCROLL_INTERVAL = 40;
export default {
  name: "InverterStatus",
  props: {
    mode: {
      type: String,
      default: "inverter",
      validator: (v) => v === "inverter",
    },
    changesFromFilter: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      pageLoading: true,
      isFetching: false,
      isRateLimited: false,
      allPlants: [],
      plantById: {},
      plantTree: {},
      openedPlants: {},
      expandedInvertersPlants: {},
      batchOffset: 0,
      totalPlants: 0,
      hasMorePlants: true,
      isLoadingBatch: false,
      visibleCount: BATCH_LIMIT,
      autoScroll: (() => { const v = localStorage.getItem('inverterStatusAutoScroll'); return v !== null ? v === 'true' : true; })(),
      isScrollingToTop: false,
      mqttBound: false,
      subscribedPlantIds: [],
      inverterStatusByPlant: {},
      hoverTooltip: {
        visible: false,
        style: {},
        title: "",
        acPower: "--",
        dcPower: "--",
      },
    };
  },
  created() {
    this._scrollTimer = null;
    this._pauseTimeout = null;
    this._resumeTimeout = null;
    this._rateLimitRetryTimer = null;
    this._isProcessingRetry = false;
    this._cachedTreeRes = null;
    this._retryAttempts = 0;
    this._observer = null;
    this._manualScrollTimer = null;
    this._programmaticScroll = false;
    this._programmaticScrollTimer = null;
    this._isManualScrolling = false;
    this._scrollListener = null;
    this._onMqttMessage = null;
    this._onMqttError = null;
    this._onMqttOffline = null;
    this._onMqttReconnect = null;
  },
  computed: {
    ...mapGetters([
      "getGeneralState",
      "getSelectedPlantId",
      "getPlantList",
      "isPlantExpired",
    ]),
    userId() {
      const userData = this.getGeneralState("userData");
      return userData ? userData.userId : null;
    },
    visiblePlants() {
      return this.allPlants.slice(0, this.visibleCount);
    },
    plantListById() {
      const map = {};
      (this.getPlantList || []).forEach((plant) => {
        if (plant && plant.id) map[String(plant.id)] = plant;
      });
      return map;
    },
    activePlantIds() {
      if (!this.getPlantList) return [];
      return this.getPlantList
        .filter((p) => p && p.id && !this.isPlantExpired(p.id))
        .map((p) => p.id);
    },
  },
  watch: {
    userId: {
      immediate: true,
      handler(v) {
        if (v && !this.allPlants.length) this.loadPlants();
      },
    },
    visibleCount() {
      // Skip observer setup during 429 retry to prevent cascading batch loads
      if (this._isProcessingRetry) return;
      this.$nextTick(() => this.setupObserver());
    },
    mode: {
      immediate: true,
      handler() {
        this.rebindMqtt();
      },
    },
    getSelectedPlantId: {
      immediate: true,
      handler() {
        this.rebindMqtt();
      },
    },
    autoScroll(v) {
      localStorage.setItem('inverterStatusAutoScroll', v);
      v ? this.startAutoScroll() : this.stopAutoScroll();
    },
  },
  mounted() {
    this.startAutoScroll();
    this.bindMqttEvents();
    const contentStyleEl = document.querySelector(".content-style");
    if (contentStyleEl) contentStyleEl.classList.add("hide-outer-scrollbar");
  },
  beforeDestroy() {
    this.stopAutoScroll();
    if (this._observer) this._observer.disconnect();
    clearTimeout(this._rateLimitRetryTimer);
    const contentStyleEl = document.querySelector(".content-style");
    if (contentStyleEl) contentStyleEl.classList.remove("hide-outer-scrollbar");
    const sc = this.$refs.scrollContainer;
    if (sc && this._scrollListener)
      sc.removeEventListener("scroll", this._scrollListener);
    this.unsubscribeAllPlantTopics();
    this.unbindMqttEvents();
  },
  methods: {
    loadPlants() {
      if (this.isFetching) return;
      this.batchOffset = 0;
      this.allPlants = [];
      this.plantById = {};

      clearTimeout(this._rateLimitRetryTimer);

      this._rateLimitRetryTimer = null;
      this._isProcessingRetry = false;
      this._cachedTreeRes = null;
      this._retryAttempts = 0;
      this.isRateLimited = false;
      this.visibleCount = BATCH_LIMIT;
      this.totalPlants = this.activePlantIds.length;
      this.hasMorePlants = this.totalPlants > 0;

      this.loadPlantBatch();
    },
    loadPlantBatch(retryBatchIds) {
      if (this.isFetching || !this.hasMorePlants || !this.userId || this.isRateLimited) return;
      const isRetry = !!retryBatchIds;
      const nextBatchIds = retryBatchIds || this.activePlantIds.slice(
        this.batchOffset,
        this.batchOffset + BATCH_LIMIT,
      );
      if (!nextBatchIds.length) {
        this.hasMorePlants = false;
        return;
      }
      this.isFetching = true;
      if (this.batchOffset === 0 && !isRetry) {
        this.pageLoading = true;
      } else {
        this.isLoadingBatch = true;
      }
      this.fetchBatchPayloads(nextBatchIds)
        .then(({ treeRes, inverterRes }) => {
          const treePayload = treeRes.data || {};
          const inverterPayload = inverterRes.data || {};
          const plants = nextBatchIds
            .map((id) => {
              const plantInfo = this.plantListById[String(id)];

              if (
                !treePayload.plants ||
                !treePayload.plants[id] ||
                treePayload.plants[id].total_inverters === 0
              ) {
                return null;
              }

              return {
                plantId: id,
                name: plantInfo ? plantInfo.name : "",
                location: plantInfo
                  ? plantInfo.location.replace(/\|\|/g, ", ")
                  : "",
                totalInverters: treePayload.plants[id].total_inverters || 0,
                stats: {
                  on: 0,
                  off: 0,
                  nc: treePayload.plants[id].total_inverters || 0,
                },
                initialInverters:
                  inverterPayload.plants && inverterPayload.plants[id]
                    ? inverterPayload.plants[id]
                    : [],
              };
            }).filter(Boolean);
          plants.forEach((plant) => {
            this.plantById[String(plant.plantId)] = plant;
            this.subscribePlantTopic(plant.plantId);
            const tree = treePayload.plants && treePayload.plants[plant.plantId];
            if (tree) {
              this.$set(this.plantTree, plant.plantId, tree.blocks);
              this.initializePlantInverters(plant.plantId, tree.blocks);
            }
            this.initializePlantInverterStatusFromApi(plant);
          });
          this._retryAttempts = 0;
          this._cachedTreeRes = null;

          if (isRetry) this._isProcessingRetry = true;
          this.allPlants = this.allPlants.concat(plants);
          this.batchOffset += nextBatchIds.length;
          this.visibleCount = this.allPlants.length;
          this.hasMorePlants = this.batchOffset < this.totalPlants;
          this.pageLoading = false;
          this.isLoadingBatch = false;
          this.isFetching = false;
          if (!isRetry) {
            this.$nextTick(() => this.setupObserver());
          } else {
            this.$nextTick(() => { this._isProcessingRetry = false; });
          }
        })
        .catch((err) => {
          if (this.isRateLimitError(err)) {
            this.handleRateLimitedBatch(nextBatchIds, err);
            return;
          }

          this.finishBatchLoad(false);
        });
    },
    fetchBatchPayloads(plantIds) {
      const fetchTree = this._cachedTreeRes
        ? Promise.resolve(this._cachedTreeRes)
        : inverterStatusApi.fetchPlantTree(plantIds).then((treeRes) => {
          this._cachedTreeRes = treeRes;
          return treeRes;
        });

      return fetchTree.then((treeRes) => {
        return inverterStatusApi.fetchInverterStatus(plantIds).then((inverterRes) => {
          return { treeRes, inverterRes };
        });
      });
    },
    handleRateLimitedBatch(batchIds, err) {
      this.pageLoading = false;
      this.isLoadingBatch = false;
      this.isFetching = false;
      this.isRateLimited = true;
      this.hasMorePlants = this.batchOffset < this.totalPlants;

      if (this._retryAttempts >= 3) {
        this.isRateLimited = false;
        this.finishBatchLoad(false);
        this._retryAttempts = 0;
        this._cachedTreeRes = null;
        return;
      }
      this._retryAttempts++;

      clearTimeout(this._rateLimitRetryTimer);
      if (!this.hasMorePlants) {
        this.isRateLimited = false;
        this._rateLimitRetryTimer = null;
        return;
      }

      this._rateLimitRetryTimer = setTimeout(() => {
        this._rateLimitRetryTimer = null;
        this.isRateLimited = false;
        this.loadPlantBatch(batchIds);
      }, this.getRateLimitRetryMs(err));
    },
    finishBatchLoad(hasMorePlants) {
      this.pageLoading = false;
      this.isLoadingBatch = false;
      this.isFetching = false;
      this.isRateLimited = false;
      this.hasMorePlants = hasMorePlants;
    },
    isRateLimitError(err) {
      return err && err.response && err.response.status === 429;
    },
    getRateLimitRetryMs(err) {
      const retryStr =
        err.response && err.response.headers
          ? err.response.headers["retry-after"]
          : null;
      const retrySec = retryStr ? parseInt(retryStr, 10) : 60;
      return (Number.isNaN(retrySec) ? 60 : retrySec) * 1000 + 1000;
    },
    initializePlantInverterStatusFromApi(plant) {
      if (!plant || !plant.initialInverters || !plant.initialInverters.length)
        return;
      plant.initialInverters.forEach((inv) => {
        const invId = this.normalizeInverterId(inv);
        const status = inv.status || "inv-nc";
        this._setInverterStatus(
          plant.plantId,
          invId,
          status,
          inv.sensor_time || null,
          {
            ac_power: inv.ac_power,
            dc_power: inv.dc_power,
            sensor_time: inv.sensor_time,
          },
          false,
        );
      });
      this.syncPlantStats(plant.plantId);
    },
    rebindMqtt() {
      this.unsubscribeAllPlantTopics();
      this.unbindMqttEvents();
      this.inverterStatusByPlant = {};
      this.allPlants.forEach((plant) => {
        if (!plant.stats) {
          this.$set(plant, "stats", {
            on: 0,
            off: 0,
            nc: plant.totalInverters || 0,
          });
        } else {
          plant.stats.on = 0;
          plant.stats.off = 0;
          plant.stats.nc = plant.totalInverters || 0;
        }
      });
      this.mqttBound = false;
      this.bindMqttEvents();
    },
    bindMqttEvents() {
      if (this.mqttBound) return;
      this._onMqttMessage = (topic, message) => {
        if (!topic || !topic.startsWith("live/")) return;
        try {
          const parsed = JSON.parse(message.toString());
          const match = topic.match(/^live\/([^/]+)\/inverter$/);
          const plantId = match
            ? match[1]
            : parsed.plantId || parsed.plant_id || parsed.plant || "unknown";
          if (!Array.isArray(parsed.deviceList)) return;
          const now = Math.floor(Date.now() / 1000);
          const touchedPlants = {};
          parsed.deviceList.forEach((d) => {
            const invId =
              d.block_id && d.inverter_room && d.inverter_id
                ? String(d.block_id) +
                "-" +
                String(d.inverter_room) +
                "-" +
                String(d.inverter_id)
                : this.normalizeInverterId(d);
            const devTime = d.sensor_time ? parseInt(d.sensor_time, 10) : null;
            let status = "inv-nc";
            if (devTime && !isNaN(devTime) && now - devTime <= MQTT_TTL) {
              const acPower = d.ac_power != null ? Number(d.ac_power) : null;
              if (acPower != null && !isNaN(acPower))
                status = acPower > 0 ? "inv-on" : "inv-off";
            }
            this._setInverterStatus(plantId, invId, status, devTime, d, false);
            touchedPlants[String(plantId)] = true;
          });
          Object.keys(touchedPlants).forEach((pid) => this.syncPlantStats(pid));
        } catch (e) {
          /* silent */
        }
      };
      this._onMqttError = () => { };
      this._onMqttOffline = () => { };
      this._onMqttReconnect = () => { };
      this.$mqtt.on("message", this._onMqttMessage);
      this.$mqtt.on("error", this._onMqttError);
      this.$mqtt.on("offline", this._onMqttOffline);
      this.$mqtt.on("reconnect", this._onMqttReconnect);
      this.mqttBound = true;
    },
    normalizeInverterId(item) {
      if (!item) return "unknown";
      return String(
        item.unique_id ||
        item.uniqueId ||
        item.inverter_id ||
        item.inverterId ||
        item.deviceId ||
        item.device_id ||
        item.label ||
        item.name ||
        item.id ||
        item.uid ||
        "unknown",
      ).trim();
    },
    subscribePlantTopic(plantId) {
      const pid = String(plantId);
      if (!pid || this.subscribedPlantIds.includes(pid)) return;
      const topic = `live/${pid}/inverter`;
      try {
        this.$mqtt.subscribe(topic);
        this.subscribedPlantIds.push(pid);
      } catch (e) {
        /* silent */
      }
    },
    unsubscribePlantTopic(plantId) {
      const pid = String(plantId);
      const topic = `live/${pid}/inverter`;
      const index = this.subscribedPlantIds.indexOf(pid);
      if (index === -1) return;
      try {
        this.$mqtt.unsubscribe(topic);
        this.subscribedPlantIds.splice(index, 1);
      } catch (e) {
        /* silent */
      }
    },
    unsubscribeAllPlantTopics() {
      this.subscribedPlantIds
        .slice()
        .forEach((pid) => this.unsubscribePlantTopic(pid));
    },
    unbindMqttEvents() {
      if (!this.mqttBound) return;
      ["message", "error", "offline", "reconnect"].forEach((ev) => {
        const key = `_onMqtt${ev.charAt(0).toUpperCase() + ev.slice(1)}`;
        if (this[key]) this.$mqtt.off(ev, this[key]);
        this[key] = null;
      });
      this.mqttBound = false;
    },
    togglePlant(id) {
      this.autoScroll = false;
      this.$set(this.openedPlants, id, !this.openedPlants[id]);
    },
    setupObserver() {
      if (this._observer) this._observer.disconnect();
      const sentinel = this.$refs.sentinel;
      if (!sentinel) return;
      this._observer = new IntersectionObserver(
        ([entry]) => {
          if (
            entry.isIntersecting &&
            !this.isLoadingBatch &&
            this.hasMorePlants &&
            !this.isRateLimited
          )
            this.loadPlantBatch();
        },
        { root: this.$refs.scrollContainer, rootMargin: "100px" },
      );
      this._observer.observe(sentinel);
    },
    startAutoScroll() {
      if (this._scrollTimer) clearInterval(this._scrollTimer);
      const el = this.$refs.scrollContainer;
      if (!el) return;
      if (!this._scrollListener) {
        this._scrollListener = () => {
          if (this._programmaticScroll) return;
          this._isManualScrolling = true;
          clearTimeout(this._manualScrollTimer);
          this._manualScrollTimer = setTimeout(() => {
            this._isManualScrolling = false;
          }, 500);
        };
        el.addEventListener("scroll", this._scrollListener, { passive: true });
      }
      this._scrollTimer = setInterval(() => {
        if (
          !this.autoScroll ||
          this.isScrollingToTop ||
          this._isManualScrolling ||
          this.isRateLimited ||
          this.isLoadingBatch ||
          this.pageLoading
        )
          return;
        const el = this.$refs.scrollContainer;
        if (!el) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 5) {
          if (!this.hasMorePlants) {
            this.isScrollingToTop = true;
            clearTimeout(this._pauseTimeout);
            this._pauseTimeout = setTimeout(() => {
              if (!this.autoScroll) {
                this.isScrollingToTop = false;
                return;
              }
              this._programmaticScroll = true;
              el.scrollTo({ top: 0, behavior: "smooth" });
              clearTimeout(this._programmaticScrollTimer);
              this._programmaticScrollTimer = setTimeout(() => {
                this._programmaticScroll = false;
              }, 1200);
              clearTimeout(this._resumeTimeout);
              this._resumeTimeout = setTimeout(() => {
                this.isScrollingToTop = false;
              }, 1200);
            }, 1500);
          }
          return;
        }
        this._programmaticScroll = true;
        el.scrollTop += 1;
        clearTimeout(this._programmaticScrollTimer);
        this._programmaticScrollTimer = setTimeout(() => {
          this._programmaticScroll = false;
        }, SCROLL_INTERVAL);
      }, SCROLL_INTERVAL);
    },
    stopAutoScroll() {
      clearInterval(this._scrollTimer);
      clearTimeout(this._pauseTimeout);
      clearTimeout(this._resumeTimeout);
      clearTimeout(this._manualScrollTimer);
      clearTimeout(this._programmaticScrollTimer);
      this._scrollTimer =
        this._pauseTimeout =
        this._resumeTimeout =
        this._manualScrollTimer =
        this._programmaticScrollTimer =
        null;
      this.isScrollingToTop =
        this._isManualScrolling =
        this._programmaticScroll =
        false;
    },
    pauseAutoScroll() {
      this._isManualScrolling = true;
      clearTimeout(this._manualScrollTimer);
      this._manualScrollTimer = setTimeout(() => {
        this._isManualScrolling = false;
      }, 500);
    },
    initializePlantInverters(plantId, blocks) {
      const store = this.ensurePlantStatusStore(plantId);
      const counts = store.counts;
      blocks.forEach((block) => {
        (block.rooms || []).forEach((room) => {
          (room.inverters || []).forEach((inv) => {
            const invId = this.normalizeInverterId(inv);
            if (inv._statusKey !== invId) inv._statusKey = invId;
            if (!store.inverters[invId]) {
              this.$set(store.inverters, invId, {
                status: "inv-nc",
                lastSeen: null,
                raw: null,
              });
              counts.nc++;
            }
          });
        });
      });
      this.syncPlantStats(plantId);
    },
    ensurePlantStatusStore(plantId) {
      const pid = String(plantId);
      if (!this.inverterStatusByPlant[pid]) {
        this.$set(this.inverterStatusByPlant, pid, {
          inverters: {},
          counts: { on: 0, off: 0, nc: 0 },
        });
      }
      return this.inverterStatusByPlant[pid];
    },
    statusBucket(status) {
      if (status === "inv-on") return "on";
      if (status === "inv-off") return "off";
      return "nc";
    },
    normalizeStatusKey(inverterId) {
      return inverterId == null
        ? "unknown"
        : String(inverterId).trim() || "unknown";
    },
    syncPlantStats(plantId) {
      const pid = String(plantId);
      const plant = this.plantById[pid];
      if (!plant) return;
      const store = this.inverterStatusByPlant[pid];
      const total = plant.totalInverters || 0;
      const counts = store && store.counts ? store.counts : {};
      const on = counts.on || 0;
      const off = counts.off || 0;
      const nc = Math.max(0, total - on - off);
      if (!plant.stats) {
        this.$set(plant, "stats", { on, off, nc });
        return;
      }
      plant.stats.on = on;
      plant.stats.off = off;
      plant.stats.nc = nc;
    },
    statusClass(plantId, inverterId) {
      const store = this.inverterStatusByPlant[String(plantId)];
      const iid = this.normalizeStatusKey(inverterId);
      return store &&
        store.inverters &&
        store.inverters[iid] &&
        store.inverters[iid].status
        ? store.inverters[iid].status
        : "inv-nc";
    },
    getInverterRecord(plantId, inverterId) {
      const store = this.inverterStatusByPlant[String(plantId)];
      const iid = this.normalizeStatusKey(inverterId);
      return store && store.inverters ? store.inverters[iid] : null;
    },
    getInverterPower(plantId, inverterId, type) {
      const inv = this.getInverterRecord(plantId, inverterId);
      if (!inv || !inv.raw) return "--";
      const field = type === "ac" ? "ac_power" : "dc_power";
      return inv.raw[field] != null ? inv.raw[field] + " kW" : "--";
    },
    showHoverTooltip(event, plantId, block, room, inv) {
      if (this.expandedInvertersPlants[plantId]) {
        this.hoverTooltip.visible = false;
        return;
      }
      const record = this.getInverterRecord(plantId, inv._statusKey);
      this.hoverTooltip.title = `${block.block_name} - ${room.room_name} - ${inv.label}`;
      this.hoverTooltip.acPower =
        record && record.raw && record.raw.ac_power != null ? record.raw.ac_power + " kW" : "--";
      this.hoverTooltip.dcPower =
        record && record.raw && record.raw.dc_power != null ? record.raw.dc_power + " kW" : "--";
      this.hoverTooltip.visible = true;
      const rect = event.currentTarget.getBoundingClientRect();
      const tooltipWidth = 220;
      const useLeft =
        window.innerWidth - rect.right < tooltipWidth &&
        rect.left > tooltipWidth + 24;
      const left = useLeft
        ? Math.max(12, rect.left - tooltipWidth - 12)
        : Math.min(window.innerWidth - tooltipWidth - 12, rect.right + 12);
      this.hoverTooltip.style = {
        top: Math.max(12, rect.top - 10) + "px",
        left: left + "px",
      };
      this.$nextTick(() => {
        const el = document.querySelector(".global-inv-tooltip");
        if (el)
          this.hoverTooltip.style = {
            top: Math.max(12, rect.top - el.offsetHeight - 12) + "px",
            left: left + "px",
          };
      });
    },
    hideHoverTooltip() {
      this.hoverTooltip.visible = false;
    },
    toggleExpandInverters(plantId) {
      this.$set(
        this.expandedInvertersPlants,
        plantId,
        !this.expandedInvertersPlants[plantId],
      );
      this.hoverTooltip.visible = false;
    },
    _setInverterStatus(
      plantId,
      inverterId,
      status,
      lastSeen,
      raw,
      syncStats = true,
    ) {
      const pid = String(plantId);
      const iid = this.normalizeStatusKey(inverterId);
      const store = this.ensurePlantStatusStore(pid);
      const nextStatus = status || "inv-nc";
      if (!store.inverters[iid]) {
        this.$set(store.inverters, iid, {
          status: nextStatus,
          lastSeen: lastSeen || null,
          raw: raw || null,
        });
        store.counts[this.statusBucket(nextStatus)]++;
      } else {
        const prevStatus = store.inverters[iid].status || "inv-nc";
        if (prevStatus !== nextStatus) {
          const prevBucket = this.statusBucket(prevStatus);
          const nextBucket = this.statusBucket(nextStatus);
          if (store.counts[prevBucket] > 0) store.counts[prevBucket]--;
          store.counts[nextBucket]++;
        }
        store.inverters[iid].status = nextStatus;
        store.inverters[iid].lastSeen =
          lastSeen || store.inverters[iid].lastSeen;
        store.inverters[iid].raw = raw || store.inverters[iid].raw;
      }
      if (syncStats) this.syncPlantStats(pid);
    },
  },
};
</script>
<style scoped>
.premium-dashboard {
  background: var(--v-bg-base);
  color: #c8d8ee;
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  z-index: 1;
  font-family: "Segoe UI", Arial, sans-serif;
  min-width: 1200px;
}

.content-area {
  flex: 1;
  overflow-y: auto;
  background: var(--v-bg-base);
  scroll-behavior: smooth;
}

.content-area::-webkit-scrollbar {
  width: 6px;
}

.content-area::-webkit-scrollbar-thumb {
  background: #1a3050;
  border-radius: 4px;
}

.global-header {
  position: sticky;
  top: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  height: 60px;
  background: var(--v-primary-base);
  border-bottom: 1px solid #334079;
  box-shadow: 0px 5px 5px 0px #334079;
  box-sizing: border-box;
}

.page-title {
  font-size: 20px;
  font-weight: 700;
  color: #c8d8ee;
}

.legend {
  display: flex;
  gap: 18px;
  align-items: center;
}

.auto-scroll-ctrl {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-right: 20px;
  padding-right: 20px;
  border-right: 1px solid #1a2e48;
}

.ctrl-lbl,
.ctrl-lbl-header {
  font-size: 11px;
  font-weight: 700;
  color: #5ab4f0;
  letter-spacing: 1px;
}

.leg {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
}

.ld {
  width: 11px;
  height: 11px;
  border-radius: 50%;
}

.don {
  background: var(--v-try-base, #2ecc71);
  box-shadow: 0 0 8px rgba(33, 227, 155, 0.4);
}

.doff {
  background: var(--v-error-base, #e74c3c);
  box-shadow: 0 0 8px rgba(255, 82, 82, 0.4);
}

.dnc {
  background: #84accc;
}

.plant-list {
  padding: 16px 10px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.plant-section {
  border: 1px solid #334079;
  border-radius: 8px;
  background: var(--v-primary-base);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.plant-header {
  position: sticky;
  top: 60px;
  z-index: 90;
  background: var(--v-primary-base);
  border-left: 4px solid transparent;
  border-radius: 8px;
  transition: all 0.3s ease;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  cursor: pointer;
}

.plant-header:hover {
  filter: brightness(1.15);
}

.plant-header.open {
  background: var(--v-primary-base);
  border-left-color: #5ab4f0;
  border-radius: 8px 8px 0 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
}

.ph-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.ph-name {
  font-size: 17px;
  font-weight: 700;
  color: #c8d8ee;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ph-loc {
  font-size: 14px;
  color: #84accc;
  margin-top: 2px;
}

.ph-right {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
  flex-shrink: 0;
}

.total-text-right {
  color: #84accc;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  border-right: 1px solid #334079;
  padding-right: 12px;
}

.tot-val {
  color: #5ab4f0;
  font-size: 14px;
  font-weight: 700;
}

.toggle-arrow {
  font-size: 22px;
  color: #2a5070;
  transition: transform 0.2s;
}

.plant-header.open .toggle-arrow {
  transform: rotate(180deg);
  color: #5ab4f0;
}

.stat-pills {
  display: flex;
  gap: 8px;
  align-items: center;
}

.stat-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 5px 14px;
  width: 130px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  box-sizing: border-box;
  transition: opacity 0.3s ease;
}

.opacity-muted {
  opacity: 0.4;
}

.pill-on {
  background: rgba(46, 204, 113, 0.15);
  background: color-mix(in srgb, var(--v-try-base, #2ecc71) 15%, transparent);
  border: 1px solid rgba(46, 204, 113, 0.5);
  border: 1px solid color-mix(in srgb, var(--v-try-base, #2ecc71) 50%, transparent);
  color: #2ecc71;
  color: var(--v-try-base, #2ecc71);
}

.pill-off {
  background: rgba(231, 76, 60, 0.15);
  background: color-mix(in srgb, var(--v-error-base, #e74c3c) 15%, transparent);
  border: 1px solid rgba(231, 76, 60, 0.5);
  border: 1px solid color-mix(in srgb, var(--v-error-base, #e74c3c) 50%, transparent);
  color: #e74c3c;
  color: var(--v-error-base, #e74c3c);
}

.pill-nc {
  background: rgba(132, 172, 204, 0.15);
  background: color-mix(in srgb, #84accc 15%, transparent);
  border: 1px solid rgba(132, 172, 204, 0.5);
  border: 1px solid color-mix(in srgb, #84accc 50%, transparent);
  color: #84accc;
}

.pill-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.pd-on {
  background: #2ecc71;
  background: var(--v-try-base, #2ecc71);
}

.pd-off {
  background: #e74c3c;
  background: var(--v-error-base, #e74c3c);
}

.pd-nc {
  background: #84accc;
}

.expand-inverters-header-ctrl {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 12px;
  background: var(--v-sixth-base);
  padding: 4px 12px;
  border-radius: 20px;
  border: 1px solid #334079;
  height: 30px;
  box-sizing: border-box;
}

.plant-content {
  padding: 20px 24px;
  background: var(--v-bg-base);
  border-left: 4px solid #5ab4f0;
  border-radius: 0 0 8px 8px;
  box-shadow: inset 0 8px 20px rgba(0, 0, 0, 0.2);
}

.plant-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.block-card {
  background: var(--v-blockCard-base);
  border: 1px solid var(--v-cardBorder-base);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  padding: 18px;
  position: relative;
  overflow: visible;
  flex: 1 1 max-content;
  min-width: 300px;
}

.block-hdr {
  font-size: 15px;
  font-weight: 700;
  color: #5ab4f0;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.rooms-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.room-row {
  background: var(--v-roomRow-base);
  border: 1px solid var(--v-cardBorder-base);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 12px 14px;
  flex: 1 1 max-content;
  min-width: 250px;
  box-shadow: inset 0 2px 10px rgba(0, 0, 0, 0.1);
}

.room-lbl {
  font-size: 14px;
  font-weight: 600;
  color: #c8d8ee;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.inv-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.inv-row.expanded {
  gap: 16px;
}

.inv-btn {
  background: var(--btn-bg, #1e2c3c);
  color: var(--status-color, #84accc);
  border: 1px solid var(--btn-border, #2e404e);
  font-size: 12px;
  font-weight: 700;
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.inv-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
  color: #c8d8ee;
  border-color: #4a637a;
}

.inv-btn.inv-on {
  --status-color: #2ecc71;
  --btn-bg: rgba(46, 204, 113, 0.15);
  --btn-border: rgba(46, 204, 113, 0.5);
  box-shadow: 0 0 10px rgba(46, 204, 113, 0.15);
}

.inv-btn.inv-on:hover {
  box-shadow: 0 4px 12px rgba(46, 204, 113, 0.3);
}

.inv-btn.inv-off {
  --status-color: #e74c3c;
  --btn-bg: rgba(231, 76, 60, 0.15);
  --btn-border: rgba(231, 76, 60, 0.5);
  box-shadow: 0 0 10px rgba(231, 76, 60, 0.15);
}

.inv-btn.inv-off:hover {
  box-shadow: 0 4px 12px rgba(231, 76, 60, 0.3);
}

.inv-btn.inv-nc {
  --status-color: #84accc;
  --btn-bg: rgba(132, 172, 204, 0.15);
  --btn-border: rgba(132, 172, 204, 0.5);
}

.inv-btn.inv-nc:hover {
  box-shadow: 0 4px 12px rgba(132, 172, 204, 0.2);
}

.inv-card {
  --status-color: #84accc;
  --title-color: #84accc;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 190px;
  height: auto;
  border-radius: 10px;
  padding: 14px 16px;
  box-sizing: border-box;
  flex: 0 0 190px;
  background: var(--v-sixth-base);
  border: 1px solid #334079;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  border-left: 4px solid var(--status-color);
  color: #c8d8ee;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.inv-card.inv-on {
  --status-color: #2ecc71;
  --title-color: #2ecc71;
}

.inv-card.inv-off {
  --status-color: #e74c3c;
  --title-color: #e74c3c;
}

.inv-card.inv-nc {
  --status-color: #84accc;
  --title-color: #84accc;
}

.inv-card-hdr {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 800;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  padding-bottom: 8px;
}

.inv-card-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--title-color);
}

.inv-card-title {
  color: var(--title-color);
  letter-spacing: 0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.inv-card-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.inv-card-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
}

.inv-card-row .row-label {
  opacity: 0.8;
  font-weight: 600;
}

.inv-card-row .row-val {
  font-weight: 700;
  color: #ffffff;
}

.global-inv-tooltip {
  position: fixed;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 200px;
  max-width: calc(100vw - 24px);
  background: #061227;
  border: 1px solid #2a5a8a;
  border-radius: 10px;
  padding: 10px 12px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.6);
  color: #eef4ff;
  z-index: 2147483647;
  pointer-events: none;
}

.tooltip-title {
  font-size: 12px;
  font-weight: 800;
  color: #b7e1ff;
  letter-spacing: 0.02em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  padding-bottom: 8px;
}

.tooltip-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #d6e7ff;
}

.tooltip-row span:first-child {
  color: #84a9dc;
  font-weight: 600;
}

.tooltip-row span:last-child {
  font-weight: 700;
}

.centered-loader,
.inner-loading,
.batch-loader {
  padding: 40px;
  text-align: center;
  color: #2a5070;
  font-size: 14px;
  font-weight: 700;
}

.loader-dots {
  display: flex;
  gap: 6px;
  justify-content: center;
  margin-bottom: 12px;
}

.loader-dots span {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #2a5a8a;
  animation: bounce 0.9s infinite;
}

.loader-dots span:nth-child(2) {
  animation-delay: 0.15s;
}

.loader-dots span:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes bounce {

  0%,
  80%,
  100% {
    transform: scale(0.6);
    opacity: 0.4;
  }

  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.sentinel {
  height: 4px;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: calc(100vh - 240px);
  padding: 40px 20px;
}

.empty-state-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 48px 56px;
  border-radius: 16px;
  background: var(--v-primary-base);
  border: 1px solid #334079;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25), 0 0 60px rgba(90, 180, 240, 0.04);
  max-width: 440px;
  text-align: center;
}

.empty-state-icon-wrap {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: rgba(90, 180, 240, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: emptyPulse 3s ease-in-out infinite;
}

.empty-state-icon {
  width: 52px;
  height: 52px;
  color: #5ab4f0;
}

.empty-state-title {
  font-size: 20px;
  font-weight: 700;
  color: #c8d8ee;
  letter-spacing: 0.01em;
}

.empty-state-subtitle {
  font-size: 14px;
  color: #6b8aaa;
  line-height: 1.6;
}

@keyframes emptyPulse {

  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(90, 180, 240, 0.08);
  }

  50% {
    box-shadow: 0 0 0 12px rgba(90, 180, 240, 0.03);
  }
}
</style>
<style>
.content-style.hide-outer-scrollbar {
  overflow-y: hidden !important;
  overflow-x: auto;
}

.theme--light .premium-dashboard {
  background: #f2f5f9;
  color: #1f2937;
}

.theme--light .content-area {
  background: #f8fafc;
}

.theme--light .global-header {
  background: #ffffff;
  border-bottom-color: #e2e8f0;
}

.theme--light .page-title {
  color: #0f172a;
}

.theme--light .ctrl-lbl,
.theme--light .ctrl-lbl-header {
  color: #2563eb;
}

.theme--light .leg {
  color: #334155;
}

.theme--light .auto-scroll-ctrl {
  border-right-color: #e2e8f0;
}

.theme--light .plant-section {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.05);
}

.theme--light .plant-header {
  background: #ffffff;
  border-bottom: none;
}

.theme--light .ph-name {
  color: #0f172a;
}

.theme--light .ph-loc {
  color: #475569;
}

.theme--light .expand-inverters-header-ctrl {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.theme--light .total-text-right {
  color: #475569;
  border-right-color: #cbd5e1;
}

.theme--light .tot-val {
  color: #2563eb;
}

.theme--light .plant-header.open {
  background: #f1f5f9;
  border-left-color: #3b82f6;
  border-bottom-color: transparent;
}

.theme--light .plant-content {
  background: #f1f5f9;
  border-left: 4px solid #3b82f6;
  border-bottom: none;
}

.theme--light .block-card {
  background: #e2e8f0;
  border-color: #cbd5e1;
  box-shadow: inset 0 2px 8px rgba(15, 23, 42, 0.05);
}

.theme--light .room-row {
  background: #ffffff;
  border-color: #cbd5e1;
}

.theme--light .inv-btn {
  --status-color: #0f172a;
  --btn-bg: #eff6ff;
  --btn-border: #cbd5e1;
}

.theme--light .inv-btn.inv-on {
  --status-color: #14532d;
  --btn-bg: #dcfce7;
  --btn-border: #4ade80;
}

.theme--light .inv-btn.inv-off {
  --status-color: #7f1d1d;
  --btn-bg: #fee2e2;
  --btn-border: #f87171;
}

.theme--light .inv-btn.inv-nc {
  --status-color: #334155;
  --btn-bg: #e2e8f0;
  --btn-border: #cbd5e1;
}

.theme--light .inv-card {
  background: #ffffff;
  color: #334155;
}

.theme--light .inv-card.inv-on {
  --status-color: #4ade80;
  --title-color: #166534;
}

.theme--light .inv-card.inv-off {
  --status-color: #fca5a5;
  --title-color: #991b1b;
}

.theme--light .inv-card.inv-nc {
  --status-color: #cbd5e1;
  --title-color: #475569;
}

.theme--light .inv-card-row .row-val {
  color: #0f172a;
}

.theme--light .inv-card-hdr {
  border-bottom-color: rgba(15, 23, 42, 0.08);
}

.theme--light .inner-loading {
  background: #ffffff;
  color: #334155;
  border: 1px solid #e2e8f0;
}

.theme--light .batch-loader {
  background: #f8fafc;
  color: #334155;
  border: 1px solid #e2e8f0;
}

.theme--light .loader-dots span {
  background: #60a5fa;
}

.theme--light .global-inv-tooltip {
  background: #ffffff;
  border-color: #cbd5e1;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.12);
  color: #0f172a;
}

.theme--light .tooltip-title {
  color: #2563eb;
  border-bottom-color: rgba(15, 23, 42, 0.08);
}

.theme--light .tooltip-row {
  color: #334155;
}

.theme--light .tooltip-row span:first-child {
  color: #475569;
}

.theme--light .tooltip-row span:last-child {
  color: #0f172a;
}

.theme--light .empty-state-card {
  background: #ffffff;
  border-color: #e2e8f0;
  box-shadow: 0 8px 32px rgba(15, 23, 42, 0.08), 0 0 60px rgba(37, 99, 235, 0.04);
}

.theme--light .empty-state-icon-wrap {
  background: rgba(37, 99, 235, 0.06);
}

.theme--light .empty-state-icon {
  color: #3b82f6;
}

.theme--light .empty-state-title {
  color: #0f172a;
}

.theme--light .empty-state-subtitle {
  color: #64748b;
}
</style>
